
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import moment from 'moment';
import { getTableName } from '@/worker/db/sqlHelpers';
import FilterWidget from '../shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import gdbx from '@/store/modules/gdbx';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
	components: {
		AmountDisplayerFilterable,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class PurchaseCurrentMonthPurchaseNumberDisplayer extends FilterWidget {
	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = [];

	public salesCount = 0;
	public purchaseAmount = '' || 0;

	public get permissionIds(): PermissionsGroup[] {
		return ['purchases'];
	}

	public get currentYear() {
		return moment(this.selectedAsOfDate).year();
	}

	public get currentMonthRange(): [number, number] {
		return [
			moment(this.selectedAsOfDate).startOf('month').valueOf(),
			moment(this.selectedAsOfDate).endOf('month').valueOf(),
		];
	}

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get decimal() {
		return gdbx.numDecimal;
	}

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			selectedSuppliers,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			selectedAsOfDate,
			selectedAgents,
			selectedStockItems,
			selectedSuppliers,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.3);

		const queryHead = await this._ensureTablesLoaded(['purchases', 'purchaseReturn', 'supplierTrades'] as any[]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.9);

		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
		const dateFilter = `TRY_CAST("DOCDATE" AS BIGINT) >= ${this.selectedDateRange[0]} AND TRY_CAST("DOCDATE" AS BIGINT) <= ${this.selectedDateRange[1]}`;

		const allSupplierLen = (gdbx.allSuppliers && gdbx.allSuppliers.length) || 0;
		const supplierFilter = this.selectedSuppliers && this.selectedSuppliers.length > 0
			&& this.selectedSuppliers.length !== allSupplierLen
			? ` AND "CODE" IN (${this.selectedSuppliers.map((s: string) => `'${escapeStr(s)}'`).join(', ')})`
			: '';

		const allAgentLen = (gdbx.allAgents && gdbx.allAgents.length) || 0;
		const agentFilter = this.selectedAgents && this.selectedAgents.length > 0
			&& this.selectedAgents.length !== allAgentLen
			? ` AND "AGENT" IN (${this.selectedAgents.map((a: string) => `'${escapeStr(a)}'`).join(', ')})`
			: '';

		let purchaseAmount = 0;

		if (this.slPhModuleOnly) {
			// PH Mode - use purchases + purchaseReturn
			const docTypes = this.includeDebitNote ? ['PI', 'CP', 'SD'] : ['PI', 'CP'];
			const docTypeFilter = ` AND "DOCTYPE" IN (${docTypes.map((d) => `'${d}'`).join(', ')})`;

			const phResult = await queryHead.rawQuery(`
				SELECT COALESCE(SUM(TRY_CAST("AMOUNT" AS DOUBLE)), 0) as total
				FROM ${getTableName('purchases')}
				WHERE ${dateFilter}${docTypeFilter}${supplierFilter}${agentFilter}
			`);
			this.checkInterrupted(jobId);
			const phAmount = (phResult[0] && Number(phResult[0].total)) || 0;

			let cnAmount = 0;
			if (this.includeCreditNote) {
				const cnResult = await queryHead.rawQuery(`
					SELECT COALESCE(SUM(TRY_CAST("AMOUNT" AS DOUBLE)), 0) as total
					FROM ${getTableName('purchaseReturn')}
					WHERE ${dateFilter}${supplierFilter}${agentFilter}
				`);
				this.checkInterrupted(jobId);
				cnAmount = (cnResult[0] && Number(cnResult[0].total)) || 0;
			}

			purchaseAmount = phAmount - cnAmount;
		} else {
			// AP Mode - use supplierTrades
			const docTypes = this.includeDebitNote ? ['PI', 'SD'] : ['PI'];
			const docTypeFilter = ` AND "DOCTYPE" IN (${docTypes.map((d) => `'${d}'`).join(', ')})`;

			const apResult = await queryHead.rawQuery(`
				SELECT COALESCE(SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)), 0) as total
				FROM ${getTableName('supplierTrades')}
				WHERE ${dateFilter}${docTypeFilter}${supplierFilter}${agentFilter}
			`);
			this.checkInterrupted(jobId);
			const apAmount = (apResult[0] && Number(apResult[0].total)) || 0;

			let cnAmount = 0;
			if (this.includeCreditNote) {
				const cnResult = await queryHead.rawQuery(`
					SELECT COALESCE(SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)), 0) as total
					FROM ${getTableName('supplierTrades')}
					WHERE ${dateFilter} AND "DOCTYPE" = 'SC'${supplierFilter}${agentFilter}
				`);
				this.checkInterrupted(jobId);
				cnAmount = (cnResult[0] && Number(cnResult[0].total)) || 0;
			}

			purchaseAmount = apAmount - cnAmount;
		}

		this.purchaseAmount = purchaseAmount;

		this.generateLoadingText(1);
		this.saveHistory('purchaseAmount');
	}
}
